import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import HeaderSection from "../../components/HeaderSection";
import { assignBulkSalary, assignSalary, getEmployees, getLeaders } from "../../http";
import "react-datepicker/dist/react-datepicker.css";
import BulkSalaryUploadModal from "./BulkSalaryUploadModal";
import * as XLSX from "xlsx";

const AssignSalary = () => {
  const SALARY_TEMPLATE_COLUMNS = [
  { header: "Employee Name", key: "name" },
  
  { header: "Basic Salary", key: "basic" },
  { header: "HRA", key: "hra" },
  { header: "Medical Allowance", key: "medicalAllowance" },
  { header: "Conveyance Allowance", key: "conveyanceAllowance" },
  { header: "Book & Periodic Allowance", key: "bookAllowance" },
  { header: "Special Allowance", key: "specialPeriodicAllowance" },
  { header: "Gratuity", key: "gratuity" },
  { header: "Fuel & Maintenance", key: "fuelAndMaintenance" },
  { header: "Travel Assistance", key: "travelAssistance" },
  { header: "Total Salary", key: "salary" },
  { header: "Employer PF", key: "employerPf" },
  { header: "Employee PF", key: "employeePf" },
  { header: "PLI", key: "pli" },
  { header: "Others", key: "others" },
  { header: "TDS", key: "tds" },
  { header: "PT", key: "pt" },
  { header: "Gratuity Deduction", key: "gratuityDeduction" },
  { header: "Other Deduction", key: "otherDeduction" },
  { header: "Loan/Advance", key: "loanAdvance" },
  { header: "Room Rent", key: "roomRent" },
  { header: "Insurance", key: "insurance" },
  { header: "Total Deduction", key: "totalDeduction" },
  { header: "Net Payable Salary", key: "payableSalary" },
];

const NUMERIC_COLUMNS = new Set(["basic","hra","medicalAllowance","conveyanceAllowance","bookAllowance","specialPeriodicAllowance","gratuity","fuelAndMaintenance","travelAssistance","salary","employerPf","employeePf","pli","others","tds","pt","gratuityDeduction","otherDeduction","loanAdvance","roomRent","insurance","totalDeduction","payableSalary"]);
  const initialState = {
    basic: "",
    hra: "",
    medicalAllowance: "",
    conveyanceAllowance: "",
    bookAllowance: "",
    specialPeriodicAllowance: "",
    gratuity: "",
    fuelAndMaintenance: "",
    travelAssistance: "", // Updated to match schema
    employerPf: "",
    pli: "", // Added from schema
    employeePf: "",
    tds: "",
    totalDeduction: "",
    salary: "", // For UI calculations only
    payableSalary: "", // For UI display only
    isTdsApplicable: false,
    gratuityDeduction: "",

    others: "", // new
    otherDeduction: "", // renamed from roomRent
    loanAdvance: "", // new
    roomRent: "",
    insurance: "",
    pt: "",
  };

  const [formData, setFormData] = useState(initialState);
  const [selectedEmployee, setSelectedEmployee] = useState();
  const [employees, setEmployees] = useState();
  const [showDeductionForm, setShowDeductionForm] = useState(false);
  const [excelData, setExcelData] = useState([]);
  const [fileName, setFileName] = useState("");
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

  useEffect(() => {
    const fetchEmployees = async () => {
      const emps = await getEmployees();
      const leaders = await getLeaders();
      setEmployees([...emps.data, ...leaders.data]);
    };
    fetchEmployees();
  }, []);

  const inputEvent = (e) => {
    const { name, value } = e.target;
    setFormData((old) => {
      return {
        ...old,
        [name]: value,
      };
    });
  };

  // Calculate total salary based on components
  const calculateTotalSalary = () => {
    const basic = parseFloat(formData.basic) || 0;
    const hra = parseFloat(formData.hra) || 0;
    const medicalAllowance = parseFloat(formData.medicalAllowance) || 0;
    const conveyanceAllowance = parseFloat(formData.conveyanceAllowance) || 0;
    const bookAllowance = parseFloat(formData.bookAllowance) || 0;
    
    const specialPeriodicAllowance =
      parseFloat(formData.specialPeriodicAllowance) || 0;
    
    const gratuity = parseFloat(formData.gratuity) || 0;
    const fuelAndMaintenance = parseFloat(formData.fuelAndMaintenance) || 0;
    const travelAssistance = parseFloat(formData.travelAssistance) || 0;  
    const pli = parseFloat(formData.pli) || 0;
    const others = parseFloat(formData.others) || 0;

    // Calculate employer PF (12% of basic)
    const employerPf = basic * 0.12;

    // Calculate total salary including employer PF
    const totalSalary =
      basic +
      hra +
      medicalAllowance +
      conveyanceAllowance +
      bookAllowance +
      specialPeriodicAllowance +
      pli +
      others +
      employerPf+
      gratuity+
      travelAssistance+
      fuelAndMaintenance
      ; // Added employerPf here

    // Update salary and employerPf fields
    setFormData((prev) => ({
      ...prev,
      salary: totalSalary.toFixed(2).toString(),
      employerPf: employerPf.toFixed(2).toString(),
    }));

    return totalSalary;
  };

  // Calculate tax deductions
  const calculateDeductions = () => {
    const basic = parseFloat(formData.basic) || 0;
    const totalSalary = parseFloat(formData.salary) || 0;

    const employeePf = basic * 0.12;
    const employerPf = basic * 0.12;

    const tds = formData.isTdsApplicable ? totalSalary * 0.1 : 0;

    const otherDeduction = parseFloat(formData.otherDeduction) || 0;
    const loanAdvance = parseFloat(formData.loanAdvance) || 0;
    const gratuityDeduction = parseFloat(formData.gratuityDeduction) || 0;
    const roomRent = parseFloat(formData.roomRent) || 0;
    const insurance = parseFloat(formData.insurance) || 0;

    const baseDeduction =
      employeePf +
      employerPf +
      tds +
      otherDeduction +
      loanAdvance +
      gratuityDeduction +
      roomRent +
      insurance;

    const payableBeforePt = totalSalary - baseDeduction;
    const pt = payableBeforePt > 15000 ? 200 : 0;

    const totalDeduction = baseDeduction + pt;
    const payableSalary = totalSalary - totalDeduction;

    setFormData((prev) => ({
      ...prev,
      employeePf: employeePf.toFixed(2).toString(),
      tds: tds.toFixed(2).toString(),
      pt: pt.toFixed(2).toString(),
      totalDeduction: totalDeduction.toFixed(2).toString(),
      payableSalary: payableSalary.toFixed(2).toString(),
    }));

    return { totalDeduction };
  };

  // Auto-calculate salary whenever component fields change
  useEffect(() => {
    calculateTotalSalary();
    // eslint-disable-next-line
  }, [
    formData.basic,
    formData.hra,
    formData.medicalAllowance,
    formData.conveyanceAllowance,
    formData.bookAllowance,
    formData.specialPeriodicAllowance,
    formData.gratuity,
    formData.fuelAndMaintenance,
    formData.travelAssistance,
    formData.pli,
    formData.others,
  ]);

  // Calculate deductions when related fields change
  useEffect(() => {
    if (formData.salary && showDeductionForm) {
      calculateDeductions();
    }
    // eslint-disable-next-line
  }, [
    formData.salary,
    formData.basic,
    formData.otherDeduction,
    formData.isTdsApplicable,
    formData.loanAdvance,
    formData.gratuityDeduction,
    formData.roomRent,
    formData.insurance,
  ]);

  const handleSalaryFormSubmit = (e) => {
    e.preventDefault();

    // Validation
    if (
      !formData.basic ||
      !formData.hra ||
      !formData.medicalAllowance ||
      !formData.conveyanceAllowance ||
      !formData.bookAllowance ||
      !formData.specialPeriodicAllowance ||
      !formData.gratuity ||
      !formData.fuelAndMaintenance ||
      !formData.travelAssistance ||
      !selectedEmployee
    ) {
      return toast.error("All Fields Required");
    }

    // Calculate final values
    calculateTotalSalary();

    // Show deduction form
    setShowDeductionForm(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    // Final calculations
    // eslint-disable-next-line
    const { totalDeduction } = calculateDeductions();

    // Prepare data matching the schema
    const dataToSend = {
      employeeID: selectedEmployee,
      basic: parseFloat(formData.basic),
      hra: parseFloat(formData.hra),
      medicalAllowance: parseFloat(formData.medicalAllowance),
      conveyanceAllowance: parseFloat(formData.conveyanceAllowance),
      bookAllowance: parseFloat(formData.bookAllowance),
      specialPeriodicAllowance: parseFloat(formData.specialPeriodicAllowance),
      gratuity: parseFloat(formData.gratuity) || 0,
      fuelAndMaintenance: parseFloat(formData.fuelAndMaintenance) || 0,
      travelAssistance: parseFloat(formData.travelAssistance) || 0,
      salary: parseFloat(formData.salary),
      payableSalary: parseFloat(formData.payableSalary),
      employerPf: parseFloat(formData.employerPf) || 0,
      others: parseFloat(formData.others) || 0,
      pli: parseFloat(formData.pli) || 0,
      employeePf: parseFloat(formData.employeePf),
      tds: parseFloat(formData.tds),
      gratuityDeduction: parseFloat(formData.gratuityDeduction) || 0,
      roomRent: parseFloat(formData.roomRent) || 0,
      insurance: parseFloat(formData.insurance) || 0,
      
      otherDeduction: parseFloat(formData.otherDeduction) || 0,
      loanAdvance: parseFloat(formData.loanAdvance) || 0,
      pt: parseFloat(formData.pt) || 0,
      totalDeduction: parseFloat(formData.totalDeduction),
    };

    console.log("Submitting salary data:", dataToSend);

    try {
      const res = await assignSalary(dataToSend);
      const { success } = res;
      if (success) {
        toast.success("Salary Assigned!");
      }
      setFormData(initialState);
      setShowDeductionForm(false);
    } catch (error) {
      console.error("Error assigning salary:", error);
      toast.error("Failed to assign salary");
    }
  };
  const downloadTemplate = () => {
  const headerRow = SALARY_TEMPLATE_COLUMNS.map((col) => col.header);
  const worksheet = XLSX.utils.aoa_to_sheet([headerRow]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Salaries");
  XLSX.writeFile(
    workbook,
    `salary_template_${new Date().toISOString().slice(0, 10)}.xlsx`
  );
};

const normalizeBulkRow = (row) => {
  const normalized = {};
  SALARY_TEMPLATE_COLUMNS.forEach(({ header, key }) => {
    if (!key) return;
    const raw = row[header];
    if (raw === undefined || raw === null || raw === "") {
      normalized[key] = NUMERIC_COLUMNS.has(key) ? 0 : "";
      return;
    }
    normalized[key] = NUMERIC_COLUMNS.has(key) ? Number(raw) || 0 : String(raw);
  });
  return normalized;
};

  const handleFileUpload = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  setFileName(file.name);

  const reader = new FileReader();
  reader.onload = (e) => {
    const data = new Uint8Array(e.target.result);
    const workbook = XLSX.read(data, { type: "array" });

    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const parsedData = XLSX.utils.sheet_to_json(sheet, { defval: "" });

    const normalized = parsedData
      .map(normalizeBulkRow)
      .filter((row) => row.name && row.name.trim() !== "");

    setExcelData(normalized);
    console.log("Normalized data:", normalized);
  };
  reader.readAsArrayBuffer(file);
};

  const handleUpload = async () => {
  if (excelData.length === 0) {
    toast.error("No data to upload!");
    return;
  }
  try {
    const response = await assignBulkSalary({ salaries: excelData });
    if (response.success) {
      toast.success(response.message);
      setShowBulkUploadModal(false);
      setFileName("");
      setExcelData([]);
    } else {
      toast.error(response.message);
    }
  } catch (error) {
    console.error("Upload failed:", error);
    toast.error("Failed to upload data");
  }
};

  return (
    <>
      <div className="main-content">
        <section className="section">
          <HeaderSection title="Salary Assignment" />

          {/* Salary Structure Form */}
          {!showDeductionForm && (
            <div className="card">
              <div className="card-header d-flex flex-row justify-content-between" style={{ height: "100%" }}>
                <h4>Detailed Salary Structure</h4>

                <div className="p-2 d-flex justify-content-center align-items-center">
                  <button onClick={() => setShowBulkUploadModal(true)} className="btn btn-primary btn-lg">
                    <i className="fas fa-file-upload mr-2"></i> Upload Files
                  </button>
                </div>
              </div>
              <div className="card-body pr-5 pl-5 m-1">
                <form
                  className="row"
                  onSubmit={handleSalaryFormSubmit}
                  id="salaryForm">
                  <div className="form-group col-md-6">
                    <label>Select Employee</label>
                    <select
                      className="form-control select2"
                      value={selectedEmployee}
                      onChange={(e) => setSelectedEmployee(e.target.value)}>
                      <option value="">-- Select Employee --</option>
                      {employees?.map((employee) => (
                        <option key={employee._id} value={employee.id}>
                          {employee.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6 mb-4">
                    <label className="d-block">Total Calculated Salary</label>
                    <h3 className="text-primary">
                      ₹ {parseFloat(formData.salary || 0).toLocaleString()}
                    </h3>
                  </div>

                  <div className="col-md-12">
                    <h5 className="text-muted mb-3">Earnings Components</h5>
                  </div>

                  {/* Salary Components */}
                  <div className="form-group col-md-4">
                    <label>Basic Salary</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">₹</div>
                      </div>
                      <input
                        onChange={inputEvent}
                        value={formData.basic}
                        type="number"
                        id="basic"
                        name="basic"
                        className="form-control"
                        placeholder="Enter basic salary"
                      />
                    </div>
                  </div>

                  <div className="form-group col-md-4">
                    <label>HRA</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">₹</div>
                      </div>
                      <input
                        onChange={inputEvent}
                        value={formData.hra}
                        type="number"
                        id="hra"
                        name="hra"
                        className="form-control"
                        placeholder="Enter HRA amount"
                      />
                    </div>
                  </div>

                  <div className="form-group col-md-4">
                    <label>Medical Allowance</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">₹</div>
                      </div>
                      <input
                        onChange={inputEvent}
                        value={formData.medicalAllowance}
                        type="number"
                        id="medicalAllowance"
                        name="medicalAllowance"
                        className="form-control"
                        placeholder="Enter medical allowance"
                      />
                    </div>
                  </div>

                  <div className="form-group col-md-4">
                    <label>Conveyance Allowance</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">₹</div>
                      </div>
                      <input
                        onChange={inputEvent}
                        value={formData.conveyanceAllowance}
                        type="number"
                        id="conveyanceAllowance"
                        name="conveyanceAllowance"
                        className="form-control"
                        placeholder="Enter conveyance allowance"
                      />
                    </div>
                  </div>

                  <div className="form-group col-md-4">
                    <label>Book and Periodic Allowance</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">₹</div>
                      </div>
                      <input
                        onChange={inputEvent}
                        value={formData.bookAllowance}
                        type="number"
                        id="bookAllowance"
                        name="bookAllowance"
                        className="form-control"
                        placeholder="Enter book allowance"
                      />
                    </div>
                  </div>

                  <div className="form-group col-md-4">
                    <label>Special Allowance</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">₹</div>
                      </div>
                      <input
                        onChange={inputEvent}
                        value={formData.specialPeriodicAllowance}
                        type="number"
                        id="specialPeriodicAllowance"
                        name="specialPeriodicAllowance"
                        className="form-control"
                        placeholder="Enter special periodic allowance"
                      />
                    </div>
                  </div>

                    <div className="form-group col-md-4">
                    <label>Gratutity</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">₹</div>
                      </div>
                      <input
                        onChange={inputEvent}
                        value={formData.gratuity}
                        type="number"
                        id="gratuity"
                        name="gratuity"
                        className="form-control"
                        placeholder="Enter Gratuity amount"
                      />
                    </div>
                  </div>

                    <div className="form-group col-md-4">
                    <label>Fuel and Maintenance</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">₹</div>
                      </div>
                      <input
                        onChange={inputEvent}
                        value={formData.fuelAndMaintenance}
                        type="number"
                        id="fuelAndMaintenance"
                        name="fuelAndMaintenance"
                        className="form-control"
                        placeholder="Enter fuel and maintenance amount"
                      />
                    </div>
                  </div>

                    <div className="form-group col-md-4">
                    <label>Travel Assistance</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">₹</div>
                      </div>
                      <input
                        onChange={inputEvent}
                        value={formData.travelAssistance}
                        type="number"
                        id="travelAssistance"
                        name="travelAssistance"
                        className="form-control"
                        placeholder="Enter Travel Assistance amount"
                      />
                    </div>
                  </div>

                  <div className="col-md-12">
                    <h5 className="text-muted mb-3">Additional Components</h5>
                  </div>

                  {/* Employer PF (read-only) */}
                  <div className="form-group col-md-4">
                    <label>Employer's PF Contribution</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">₹</div>
                      </div>
                      <input
                        value={formData.employerPf}
                        type="text"
                        id="employerPf"
                        name="employerPf"
                        className="form-control"
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="form-group col-md-4">
                    <label>PLI</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">₹</div>
                      </div>
                      <input
                        onChange={inputEvent}
                        value={formData.pli}
                        type="number"
                        id="pli"
                        name="pli"
                        className="form-control"
                        placeholder="Enter PLI amount"
                      />
                    </div>
                  </div>
                  <div className="form-group col-md-4">
                    <label>Others</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">₹</div>
                      </div>
                      <input
                        onChange={inputEvent}
                        value={formData.others}
                        type="number"
                        id="others"
                        name="others"
                        className="form-control"
                        placeholder="Enter other allowances"
                      />
                    </div>
                  </div>

                  <div className="form-group text-center col-md-12 mt-4">
                    <button className="btn btn-primary btn-lg" type="submit">
                      <i className="fas fa-arrow-right mr-2"></i>
                      Continue to Deductions
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Tax Deductions Form */}
          {showDeductionForm && (
            <div className="card">
              <div className="card-header">
                <h4>Tax Deductions</h4>
              </div>
              <div className="card-body pr-5 pl-5 m-1">
                <form className="row" onSubmit={onSubmit} id="deductionForm">
                  <div className="col-md-6 mb-4">
                    <label className="d-block">Total Salary</label>
                    <h3 className="text-success">
                      ₹ {parseFloat(formData.salary || 0).toLocaleString()}
                    </h3>
                  </div>

                  <div className="col-md-6 mb-4">
                    <label className="d-block">Final Payable Salary</label>
                    <h3 className="text-primary">
                      ₹{" "}
                      {parseFloat(formData.payableSalary || 0).toLocaleString()}
                    </h3>
                  </div>

                  <div className="col-md-12">
                    <h5 className="text-muted mb-3">Deduction Components</h5>
                  </div>

                  {/* Employee PF */}
                  <div className="form-group col-md-4">
                    <label>Employee PF (12% of Basic)</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">₹</div>
                      </div>
                      <input
                        value={formData.employeePf}
                        type="text"
                        id="employeePf"
                        name="employeePf"
                        className="form-control"
                        readOnly
                      />
                    </div>
                  </div>

                  {/* Employer PF */}
                  <div className="form-group col-md-4">
                    <label>Employer PF (12% of Basic)</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">₹</div>
                      </div>
                      <input
                        value={formData.employerPf}
                        type="text"
                        id="employerPfDeduction"
                        name="employerPfDeduction"
                        className="form-control"
                        readOnly
                      />
                    </div>
                  </div>

                  {/* TDS */}
                  <div className="form-group col-md-4">
                    <label>TDS (10% if applicable)</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">₹</div>
                      </div>
                      <input
                        value={formData.tds}
                        type="text"
                        id="tds"
                        name="tds"
                        className="form-control"
                        readOnly
                      />
                    </div>
                  </div>

                  {/* TDS Applicability */}
                  <div className="form-group col-md-4">
                    <label>TDS Applicable?</label>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="isTdsApplicable"
                        checked={formData.isTdsApplicable}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            isTdsApplicable: e.target.checked,
                          }))
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="isTdsApplicable">
                        Apply TDS (10% of total salary)
                      </label>
                    </div>
                  </div>

                  {/* PT (Professional Tax) */}
                  <div className="form-group col-md-4">
                    <label>PT (Professional Tax)</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">₹</div>
                      </div>
                      <input
                        value={formData.pt}
                        type="text"
                        id="pt"
                        name="pt"
                        className="form-control"
                        readOnly
                      />
                    </div>
                    <small className="form-text text-muted">
                      ₹200 if payable salary &gt; ₹15,000, else ₹0
                    </small>
                  </div>

                  {/* Other Deduction */}
                  <div className="form-group col-md-4">
                    <label>Other Deduction</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">₹</div>
                      </div>
                      <input
                        onChange={inputEvent}
                        value={formData.otherDeduction}
                        type="number"
                        id="otherDeduction"
                        name="otherDeduction"
                        className="form-control"
                        placeholder="Enter other deductions"
                      />
                    </div>
                  </div>

                  {/* Gratuity Deduction */}
                  <div className="form-group col-md-4">
                    <label>Gratuity Deduction</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">₹</div>
                      </div>
                      <input
                        onChange={inputEvent}
                        value={formData.gratuityDeduction}
                        type="number"
                        id="gratuityDeduction"
                        name="gratuityDeduction"
                        className="form-control"
                        placeholder="Enter gratuity deduction"
                      />
                    </div>
                  </div>

                  {/* Room Rent */}
                  <div className="form-group col-md-4">
                    <label>Room Rent</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">₹</div>
                      </div>
                      <input
                        onChange={inputEvent}
                        value={formData.roomRent}
                        type="number"
                        id="roomRent"
                        name="roomRent"
                        className="form-control"
                        placeholder="Enter room rent deduction"
                      />
                    </div>
                  </div>

                  {/* Insurance */}
                  <div className="form-group col-md-4">
                    <label>Insurance</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">₹</div>
                      </div>
                      <input
                        onChange={inputEvent}
                        value={formData.insurance}
                        type="number"
                        id="insurance"
                        name="insurance"
                        className="form-control"
                        placeholder="Enter insurance deduction"
                      />
                    </div>
                  </div>

                  {/* Loan/Advance */}
                  <div className="form-group col-md-4">
                    <label>Loan/Advance</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">₹</div>
                      </div>
                      <input
                        onChange={inputEvent}
                        value={formData.loanAdvance}
                        type="number"
                        id="loanAdvance"
                        name="loanAdvance"
                        className="form-control"
                        placeholder="Enter loan/advance amount"
                      />
                    </div>
                  </div>

                  {/* Total Deduction */}
                  <div className="form-group col-md-4">
                    <label>Total Deduction</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <div className="input-group-text">₹</div>
                      </div>
                      <input
                        value={formData.totalDeduction}
                        type="text"
                        id="totalDeduction"
                        name="totalDeduction"
                        className="form-control"
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="form-group text-center col-md-12 mt-4">
                    <button
                      type="button"
                      className="btn btn-secondary btn-lg mr-3"
                      onClick={() => setShowDeductionForm(false)}>
                      <i className="fas fa-arrow-left mr-2"></i>
                      Back to Salary Details
                    </button>
                    <button className="btn btn-primary btn-lg" type="submit">
                      <i className="fas fa-save mr-2"></i>
                      Assign Salary
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
          <BulkSalaryUploadModal
            show={showBulkUploadModal}
            onClose={() => { setShowBulkUploadModal(false); setFileName(""); }}
            onDownloadTemplate={downloadTemplate}
            onFileChange={handleFileUpload}
            onUpload={handleUpload}
            fileName={fileName}
          />
        </section>
      </div>
    </>
    
  
  );
};

export default AssignSalary;
